import React, {useState} from 'react';
import { Link } from "react-router-dom";
import { useLazyGetSearchResultQuery } from "context/pages/pageApiSlice";
import TextField from '@mui/material/TextField';
import { nanoid } from '@reduxjs/toolkit';
import Autocomplete from '@mui/material/Autocomplete';
import { styled } from '@mui/system';
import {CircularProgress, Icon, Paper,  Popper} from '@mui/material';
import MDBox from "dashboard/components/MDBox";
import MDAvatard from "dashboard/components/MDAvatar";
import MDTypography from "dashboard/components/MDTypography";

const StyledInputBase = styled(TextField)(({ theme }) => ({
  color: 'white',
  width :"100%",
  padding : 0,
  '& .MuiInputBase-input::placeholder':{
    color:"white",
    opacity :.8
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    borderRadius: 50,
    fontSize:'1rem',
    height : "37px",
    paddingLeft:"42px !important",
    // vertical padding + font size from searchIcon
    // paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    color :"white",
    [theme.breakpoints.up('sm')]: {
      '&:focus': {
        boxShadow: "inset 0px 0px 4px rgba(0,0,0,0.5)"
      },
    },
  },
  "& .MuiOutlinedInput-notchedOutline":{
    border : 0
  }
}));

const CustomPopper = (props) => {
  return (
    <Popper 
      component={Paper} 
      {...props} 
      sx={{ 
        opacity: props.open ? 1 :0, 
        borderRadius: "1rem !important",
        width:"100%",
        position: "absolute"
       }} />
  );
};

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.secondary.main,
  marginBottom :5,
  zIndex:1000,
  fontWeight :600,
  fontSize:15,
  backgroundColor: theme.palette.background.card
}));

const Search = (props) => {
  const { parseContent } = props;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [searchResult, setSearchResult] = useState([]);
  const [serachFecth,
    {isLoading,
    isFetching}
  ] = useLazyGetSearchResultQuery({name});

  const fetchData = async name => {
    try{
      const result = await serachFecth({name}).unwrap();
      if ( result?.count !== 0 ) { 
          const searchProductList =  result.products?.map(item => ({
              name: item.productName,
              category: item.category?.name || "Autres",
              id: item.id,
              img: `${process.env.REACT_APP_PUBLIC_URL}/img/product/${item.selectedImage}`,
              description: item.description,
              type : "products",
              link : `/product/${item.id}`
          }));

          const searchPageList = result.pages?.map((item) => {
            const sections = Object.entries(item);
            const pageResult = sections.map(([key, card]) => ({
              name: card.title,
              category: card.pageName.includes('index') ? "Home page" : card.pageName,
              id: `${card.id}-${nanoid()}`,
              img: `${process.env.REACT_APP_PUBLIC_URL}/img/${key.toLowerCase()}/${card.img}`, // Replace 'clients' with the key
              description: card.text ,
              type: "pages",
              link:`${card.slug}`
            }));
            return Object.values(pageResult);
          });          
          setSearchResult(searchProductList.concat(...searchPageList));    
      }
    }catch(e){}
  }

  let typingTimeout;
  const handleSearch = event => {
    clearTimeout(typingTimeout);
    typingTimeout = setTimeout(()=> {
      setName(event.target.value);
      fetchData(event.target.value);
    }, 500);
  }
  return (
    <Autocomplete  
      id="search-select-demo"
      options={searchResult}
      open={open && name.length}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      groupBy={(option) => option.type}
      className="custom-autocomplete"
      freeSolo
      noOptionsText={'Aucune Résultat Trouvé !'}
      isOptionEqualToValue={(option, value) => option.name === value.name || option.id === value.id}
      getOptionLabel={option =>  option && option.name}
      renderGroup={params => 
        <MDBox component="li" key={nanoid()} sx={{  '& > span': { flexShrink: 0 } }} {...props}>
          <GroupHeader>{params.group} {isLoading || isFetching ? <CircularProgress color="inherit" size={20} /> : null}</GroupHeader> 
          <MDBox component="ul" sx={{p:0}}>
            {params.children}
          </MDBox>
        </MDBox>
      }
      filterOptions={(options, state) => {
        const matches = [];
        const searchValue = state.inputValue.toLowerCase();
        options.forEach((option) => {
          const name = option.name.toLowerCase();
          const description = option.description.toLowerCase();
          const category = option.category.toLowerCase();

          if (name.includes(searchValue) || description.includes(searchValue) || category.includes(searchValue) ) {
            matches.push(option);
          }
        });
    
        return matches;
      }}
      renderOption={(props, option) =>  (<MDTypography component="li" style={{paddingLeft : 5, paddingRight : 5}}  sx={{ p:1, '& > span': { mr: 1, flexShrink: 0 }}} {...props}>
          <MDBox component={Link} to={option.link} sx={{display:"flex", m:"0 !important", "&::after":{display:"none !important"}}}>
            <MDAvatard size="medium" variant="rounded"  src={option.img} sx={{maxWidth:"40px", mx : 1}} />
            <MDBox >
              <MDTypography variant="h6" >{option.name}<MDTypography color="info" sx={{px:1}} variant="caption" > ( {option.category} ) </MDTypography></MDTypography>
              <MDTypography variant="caption" sx={{display :"inline-block", maxWidth :"90%"}}  className="truncate-text">{parseContent(option.description)}</MDTypography>
            </MDBox>
          </MDBox>
        </MDTypography>)}
      PopperComponent={CustomPopper}
      size="medium"
      renderInput={(params) => (
        <MDBox sx={{ position: "relative", display :"flex", alignItems :"center", 
                    // backgroundColor : ({palette : {background}})=>open && background.card, 
                    borderTopLeftRadius :20, 
                    borderTopRightRadius :20 }}>
            <StyledInputBase
                onChange={handleSearch}
                value={name}
                {...params}
                placeholder="search"
                InputProps={{
                ...params.InputProps,
                autoComplete: 'off',
                style:{ padding : "0px", height:"42px" },
                }}
                size="small"
            />
        </MDBox>
        )}
    autoComplete={false}
    name="searchBar"
  />
  )
}

export default Search